import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftRight, Eye, FilterX } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function TransferHistory() {
  const [sp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [detail, setDetail] = useState(null);
  const [f, setF] = useState({ q: sp.get("q") || "", prev: "all", next: "all", location: "", from: "", to: "", by: "" });

  const load = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (f.q) p.set("q", f.q);
      if (f.prev !== "all") p.set("prev_franchise", f.prev);
      if (f.next !== "all") p.set("new_franchise", f.next);
      if (f.location) p.set("location", f.location);
      if (f.from) p.set("date_from", f.from);
      if (f.to) p.set("date_to", f.to);
      if (f.by) p.set("transferred_by", f.by);
      const { data } = await api.get(`/transfers?${p}`);
      setRows(data);
    } catch { toast.error("Failed to load transfers"); }
  }, [f]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/franchise").then(({ data }) => setFranchises(data)).catch(() => {}); }, []);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const clear = () => setF({ q: "", prev: "all", next: "all", location: "", from: "", to: "", by: "" });

  return (
    <div>
      <PageHeader title="Franchise Transfer History" subtitle="Every Delivery Partner franchise reassignment — fully auditable" />

      <div className="mb-5 rounded-xl border border-border bg-card p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div><Label>DP Name / FE ID</Label><Input placeholder="Search…" value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="tf-filter-q" /></div>
          <div><Label>Previous Franchise</Label>
            <Select value={f.prev} onValueChange={(v) => set("prev", v)}><SelectTrigger data-testid="tf-filter-prev"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{franchises.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>New Franchise</Label>
            <Select value={f.next} onValueChange={(v) => set("next", v)}><SelectTrigger data-testid="tf-filter-next"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{franchises.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Location</Label><Input placeholder="e.g. Delhi" value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="tf-filter-location" /></div>
          <div><Label>Transfer Date From</Label><Input type="date" value={f.from} onChange={(e) => set("from", e.target.value)} data-testid="tf-filter-from" /></div>
          <div><Label>Transfer Date To</Label><Input type="date" value={f.to} onChange={(e) => set("to", e.target.value)} data-testid="tf-filter-to" /></div>
          <div><Label>Transferred By</Label><Input placeholder="Admin name…" value={f.by} onChange={(e) => set("by", e.target.value)} data-testid="tf-filter-by" /></div>
          <div className="flex items-end"><Button variant="outline" onClick={clear} data-testid="tf-filter-clear"><FilterX className="mr-1.5 h-4 w-4" /> Clear</Button></div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Date</TableHead><TableHead>Delivery Partner</TableHead><TableHead>FE ID</TableHead>
            <TableHead>Previous Franchise</TableHead><TableHead>New Franchise</TableHead><TableHead>Transferred By</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground" data-testid="tf-empty">No transfers recorded yet</TableCell></TableRow>}
            {rows.map((t) => (
              <TableRow key={t.id} data-testid={`transfer-row-${t.id}`}>
                <TableCell>{fmtDate(t.effective_date)}{t.backdated && <span className="ml-1.5 rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-rose-500">Backdated</span>}</TableCell>
                <TableCell className="font-semibold">{t.dp_name}</TableCell>
                <TableCell className="font-mono text-xs">{t.fe_id}</TableCell>
                <TableCell>{t.previous_franchise_name || "—"}</TableCell>
                <TableCell className="text-emerald-500">{t.new_franchise_name}</TableCell>
                <TableCell>{t.transferred_by}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setDetail(t)} data-testid={`transfer-view-${t.id}`}><Eye className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-md" data-testid="transfer-detail-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-head"><ArrowLeftRight className="h-5 w-5 text-primary" /> Transfer Details</DialogTitle><DialogDescription className="sr-only">Complete audit details of this franchise transfer</DialogDescription></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              {[
                ["Delivery Partner", `${detail.dp_name} (${detail.fe_id})`],
                ["Location", detail.location || "—"],
                ["Previous Franchise", detail.previous_franchise_name ? `${detail.previous_franchise_name} · ${detail.previous_franchise_referral}` : "—"],
                ["New Franchise", `${detail.new_franchise_name} · ${detail.new_franchise_referral}`],
                ["Effective Date", `${fmtDate(detail.effective_date)} (start of date, Asia/Kolkata)`],
                ["Reason", detail.reason],
                ["Remarks", detail.remarks || "—"],
                ["Transferred By", `${detail.transferred_by}${detail.transferred_by_email ? ` (${detail.transferred_by_email})` : ""}`],
                ["Transfer Created", fmtDateTime(detail.created_at)],
                ["Confirmation Timestamp", fmtDateTime(detail.confirmed_at)],
                ["Status", detail.status],
                ["Orders Reassigned", detail.backdated ? String(detail.orders_reassigned) : "0 (future-only)"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-4 border-b border-border/50 pb-1.5">
                  <span className="text-muted-foreground">{k}</span><span className="text-right font-medium">{v}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
